import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { CheckCircle2, Plane, Calendar, MapPin, Clock, Mail, Phone, AlertCircle } from 'lucide-react';
import { airlines, calculateCompensation } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EligibilityChecker = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    airline: '',
    flightNumber: '',
    date: '',
    departure: '',
    arrival: '',
    delayLength: '',
    email: '',
    phone: ''
  });

  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const getDelayLengthLabel = (value) => {
    const labels = {
      '2': 'Less than 3 hours',
      '3': '3-4 hours',
      '4': '4-5 hours',
      '6': '5+ hours',
      'cancelled': 'Flight cancelled'
    };
    return labels[value] || value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Submit to backend API
      const response = await fetch(`${API_URL}/api/claims`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          delayLength: getDelayLengthLabel(formData.delayLength)
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit claim');
      }

      const data = await response.json();
      
      if (data.success) {
        // Calculate compensation for display
        const distance = parseInt(formData.delayLength) > 5 ? 3600 : 2000;
        const compensation = calculateCompensation({
          delayLength: parseInt(formData.delayLength) || 6,
          distance: distance
        });
        
        setResult({
          ...compensation,
          claimId: data.claim_id
        });
      } else {
        throw new Error(data.message || 'Failed to submit claim');
      }
    } catch (err) {
      console.error('Error submitting claim:', err);
      setError('Failed to submit your claim. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8">
        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Check Your Eligibility</h2>
              <p className="text-gray-600 mt-1">Get your estimated compensation in 2 minutes</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {!result ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              
              {/* Airline */}
              <div className="space-y-2">
                <Label htmlFor="airline" className="text-sm font-medium text-gray-700">
                  <Plane className="w-4 h-4 inline mr-2" />
                  Airline
                </Label>
                <Select onValueChange={(value) => handleChange('airline', value)} required>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select airline" />
                  </SelectTrigger>
                  <SelectContent>
                    {airlines.map(airline => (
                      <SelectItem key={airline} value={airline}>{airline}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Flight Number & Date */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="flightNumber" className="text-sm font-medium text-gray-700">
                    Flight Number
                  </Label>
                  <Input
                    id="flightNumber"
                    placeholder="e.g. BA123"
                    value={formData.flightNumber}
                    onChange={(e) => handleChange('flightNumber', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-sm font-medium text-gray-700">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Flight Date
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              </div>

              {/* Departure & Arrival */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="departure" className="text-sm font-medium text-gray-700">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Departure Airport
                  </Label>
                  <Input
                    id="departure"
                    placeholder="e.g. LHR London"
                    value={formData.departure}
                    onChange={(e) => handleChange('departure', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="arrival" className="text-sm font-medium text-gray-700">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Arrival Airport
                  </Label>
                  <Input
                    id="arrival"
                    placeholder="e.g. CDG Paris"
                    value={formData.arrival}
                    onChange={(e) => handleChange('arrival', e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Delay Length */}
              <div className="space-y-2">
                <Label htmlFor="delayLength" className="text-sm font-medium text-gray-700">
                  <Clock className="w-4 h-4 inline mr-2" />
                  Delay Length (hours)
                </Label>
                <Select onValueChange={(value) => handleChange('delayLength', value)} required>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select delay duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">Less than 3 hours</SelectItem>
                    <SelectItem value="3">3-4 hours</SelectItem>
                    <SelectItem value="4">4-5 hours</SelectItem>
                    <SelectItem value="6">5+ hours</SelectItem>
                    <SelectItem value="cancelled">Flight cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Contact Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone (optional)
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+44 123 456 7890"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? 'Checking Eligibility...' : 'Check My Compensation'}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                By submitting, you agree to our Terms of Service and Privacy Policy
              </p>
            </form>
          ) : (
            <div className="space-y-6">
              {result.eligible ? (
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardHeader className="text-center pb-4">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    <CardTitle className="text-2xl text-green-900">Great News!</CardTitle>
                    <CardDescription className="text-green-700 text-base">
                      You may be eligible for compensation
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="bg-white rounded-xl p-6 space-y-4">
                      <div className="text-center border-b pb-4">
                        <p className="text-sm text-gray-600 mb-2">Estimated Compensation</p>
                        <p className="text-5xl font-bold text-green-600">€{result.amount}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Our fee (25% + VAT)</p>
                          <p className="font-semibold text-gray-900">€{result.successFee}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Your payout</p>
                          <p className="font-semibold text-green-600 text-xl">€{result.yourPayout}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-900">Next Steps:</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>We'll verify your flight details and eligibility</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>We'll gather all necessary documentation</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>We'll submit your claim to the airline</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>You'll receive updates and payment within 4-8 weeks</span>
                        </li>
                      </ul>
                    </div>

                    <Button
                      onClick={onClose}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold rounded-lg"
                    >
                      Start My Claim
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-2 border-orange-200 bg-orange-50">
                  <CardHeader className="text-center">
                    <CardTitle className="text-xl text-orange-900">Not Eligible This Time</CardTitle>
                    <CardDescription className="text-orange-700">
                      {result.message}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 mb-4">
                      Your flight delay was less than 3 hours, which doesn't meet EU261/UK261 compensation requirements.
                    </p>
                    <Button
                      onClick={() => setResult(null)}
                      variant="outline"
                      className="w-full border-orange-300 text-orange-700 hover:bg-orange-100"
                    >
                      Check Another Flight
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EligibilityChecker;
